// src/pages/ChatBot/ManageChat.tsx
//
// ChatBot management console. Tabbed shell mirrors the platform admin
// dashboard (SuperAdminDashboard): a stationary tab head with one scrollable
// panel below it. Reached at the hardcoded /manage-chat route (App.js),
// gated by the chatbot.admin permission + chatbot entitlement — the admin
// console for the chatbot module after it replaced the original chatbot.
// Documents carries the full functionality of the legacy ManageContent
// page; Queries lists the query-log table backed by the query-logs admin
// API (replacing the legacy User Queries panel); Configs is the real,
// registry-driven tenant config admin surface (ConfigsPanel); Evaluation is
// the golden set + evaluation runs surface (EvalsPanel).
//
// Because a config edit is staged (Save/Cancel), leaving the Configs tab while
// it's dirty must not silently drop work. ConfigsPanel guards its own internal
// stage nav, but it can't see the top tabs — so this shell owns that guard:
// it tracks the panel's dirtiness and its registered save/discard actions, and
// intercepts a tab switch away from a dirty Configs tab with the shared
// UnsavedChangesModal.

import { useCallback, useRef, useState } from 'react';
import DocumentsPanel from './DocumentsPanel';
import QueriesPanel from './query-logs/QueriesPanel';
import ConfigsPanel from './configs/ConfigsPanel';
import EvalsPanel from './evals/EvalsPanel';
import { UnsavedChangesModal } from './configs/config-modals';
import './ManageChat.css';

type ManageChatTab = 'documents' | 'queries' | 'configs' | 'evaluation';

const TABS: { key: ManageChatTab; label: string }[] = [
  { key: 'documents', label: 'Documents' },
  { key: 'queries', label: 'Queries' },
  { key: 'configs', label: 'Configs' },
  { key: 'evaluation', label: 'Evaluation' },
];

// The save/discard hooks ConfigsPanel registers so this shell can drive them
// from the leave guard. `save` resolves true only when the save fully succeeded.
interface LeaveActions {
  save: () => Promise<boolean>;
  discard: () => void;
}

export default function ManageChat() {
  const [activeTab, setActiveTab] = useState<ManageChatTab>('documents');

  // Configs dirty-state guard wiring.
  const [configsDirty, setConfigsDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<ManageChatTab | null>(null);
  const [isGuardSaving, setIsGuardSaving] = useState(false);
  const leaveActionsRef = useRef<LeaveActions | null>(null);

  // Stable so ConfigsPanel's register effect doesn't churn every render.
  const registerLeaveActions = useCallback((actions: LeaveActions) => {
    leaveActionsRef.current = actions;
  }, []);

  // A tab switch away from a dirty Configs tab is intercepted by the guard.
  const handleTabClick = (target: ManageChatTab) => {
    if (target === activeTab || isGuardSaving) return; // ignore mid-save clicks
    if (activeTab === 'configs' && configsDirty) {
      setPendingTab(target);
      return;
    }
    setActiveTab(target);
  };

  const handleGuardSave = async () => {
    const target = pendingTab;
    // Close the guard first: ConfigsPanel's own rebuild-confirm dialog renders
    // inside the panel and would otherwise sit underneath this modal.
    setPendingTab(null);
    setIsGuardSaving(true);
    const ok = (await leaveActionsRef.current?.save()) ?? false;
    setIsGuardSaving(false);
    // Success → continue to the target tab; failure/cancel → stay put.
    if (ok && target) setActiveTab(target);
  };

  const handleGuardDiscard = () => {
    const target = pendingTab;
    leaveActionsRef.current?.discard();
    setPendingTab(null);
    if (target) setActiveTab(target);
  };

  return (
    <div className="manage-chat">
      <div className="mc-sticky-head">
        <h2 className="mc-title">Manage Chat</h2>
        <div className="mc-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`mc-tab${activeTab === t.key ? ' mc-tab-active' : ''}`}
              onClick={() => handleTabClick(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mc-tab-panel">
        {activeTab === 'documents' && <DocumentsPanel />}
        {activeTab === 'queries' && <QueriesPanel />}
        {activeTab === 'configs' && (
          <ConfigsPanel
            onDirtyChange={setConfigsDirty}
            registerLeaveActions={registerLeaveActions}
          />
        )}
        {activeTab === 'evaluation' && <EvalsPanel />}
      </div>

      {/* Top-tab leave guard for a dirty Configs tab */}
      <UnsavedChangesModal
        open={pendingTab !== null}
        isSaving={isGuardSaving}
        onSave={handleGuardSave}
        onDiscard={handleGuardDiscard}
        onKeepEditing={() => setPendingTab(null)}
      />
    </div>
  );
}
